import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useGetPaginatedCustomerSolarPlantListAdminQuery,
  useLazyViewSolarPlantStatusesQuery,
} from "../../redux/api/dashboard/dashboardAPI";
import { useLazyViewGenerationServiceProvidersQuery } from "../../redux/api/generation/generationAPI";
import { useCurrentAccess } from "../../redux/api/org-user/orgUserAPI";
import { useSiteFacets } from "../../redux/api/site-classification/siteClassificationAPI";
import {
  selectPlantStatus,
  selectSitesListFilters,
  setSelectPlantStatus,
  setSiteViewTabId,
  SitesListFilters,
  siteViewTabId,
  updateSitesListFilters,
} from "../../redux/features/sites/sites-slice";
import SiteEnergySource from "../../shared/components/site-energy-source/site-energy-source";
import SiteStatusCount from "../../shared/components/site-status-count/site-status-count";
import SitesTable from "../../shared/components/sites-table/sites-table";
import IListAllSolarPlantsRequestDTO from "../../shared/oversight-core/dtos/request-dtos/list-all-solar-plants-response-dto";
import IViewAllGenerationServiceProvidersResponseDTO from "../../shared/oversight-core/dtos/response-dtos/view-all-generation-service-providers-response-dto";
import ViewSolarPlantStatusesResponseDTO from "../../shared/oversight-core/dtos/response-dtos/view-solar-plant-statuses-response-dto";
import { EPlantStatus } from "../../shared/oversight-core/enums/plant-status";
import { ESolarPlantSize } from "../../shared/oversight-core/enums/solar-plant-size";
import { ESolarServiceProvider } from "../../shared/oversight-core/enums/solar-service-provider";
import { AppRoute } from "../../shared/oversight-core/interfaces/app-routes";
import { IGenerationServiceProviderView } from "../../shared/oversight-core/interfaces/generation-service-provider-view";
import { IInstance } from "../../shared/oversight-core/interfaces/instance";
import { IMapLocation } from "../../shared/oversight-core/interfaces/map-location";
import { IShallowSolarPlantView } from "../../shared/oversight-core/interfaces/shallow-solar-plant-view";
import ActiveInactiveIndicator from "../../shared/oversight-core/ui-elements/active-inactive-indicator/active-inactive-indicator";
import { Option } from "../../shared/oversight-core/ui-elements/app-select/app-select";
import ButtonWithBadge from "../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import MaterialIcon from "../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Pagination from "../../shared/oversight-core/ui-elements/pagination/pagination";
import Search from "../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../shared/oversight-core/ui-elements/spinner/spinner";
import TabsIconButton from "../../shared/oversight-core/ui-elements/tabs-icon-button/tabs-icon-button";
import { getServiceProviderLogo } from "../../shared/oversight-core/utils/get-service-provider-logo";
import { getSiteStatusInfo } from "../../shared/oversight-core/utils/get-site-status-info";
import { getRelativeTimeFromTimestamp } from "../../shared/oversight-core/utils/time-utils";
import { useIsGuest } from "../../shared/utils/org-roles";
import { sectorLabelOf, siteTagsOf } from "../../shared/utils/site-tags";
import AddUpdateSiteModal from "./components/add-update-site-modal/add-update-site-modal";
import ConnectPlantModal from "./components/connect-plant-modal/connect-plant-modal";
import SitesFilteringSidePanel from "./components/sites-filtering-side-panel/sites-filtering-side-panel";
import SitesMapView from "./components/sites-map-view/sites-map-view";
import styles from "./sites.module.scss";

const chartData: { value: number }[] = [
  { value: 100 },
  { value: 130 },
  { value: 210 },
  { value: 190 },
  { value: 240 },
  { value: 300 },
  { value: 220 },
  { value: 240 },
  { value: 190 },
  { value: 230 },
];

const Sites = () => {
  const isGuest = useIsGuest();
  const { data: currentAccess } = useCurrentAccess();
  const tenantId =
    currentAccess && !currentAccess.platformAdmin
      ? currentAccess.tenantId
      : undefined;
  const { data: siteFacetsData } = useSiteFacets(tenantId);
  const siteFacets = siteFacetsData?.siteFacets ?? [];
  const location = useLocation();

  const shouldUpdate = location.state?.updateCurrentState;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedTabId = useSelector(siteViewTabId);
  const selectedPlantStatus = useSelector(selectPlantStatus);
  const sitesListFilters = useSelector(selectSitesListFilters);

  // Status is the status tabs' selection; the rest is kept in the store so it outlives this page.
  const filters: IListAllSolarPlantsRequestDTO = useMemo(
    () => ({ ...sitesListFilters, plantStatus: selectedPlantStatus }),
    [sitesListFilters, selectedPlantStatus],
  );

  const setFilters = useCallback(
    (changes: Partial<SitesListFilters>) => {
      dispatch(updateSitesListFilters(changes));
    },
    [dispatch],
  );

  const [searchText, setSearchText] = useState(sitesListFilters.searchValue);
  const [showFilteringSidePanel, setShowFilteringSidePanel] = useState(false);
  const [showAddUpdateSiteModal, setShowAddUpdateSiteModal] = useState(false);
  const [showConnectPlantModal, setShowConnectPlantModal] = useState(false);
  const [locations, setLocations] = useState<IMapLocation[]>([]);
  const [solarPlantList, setSolarPlantList] = useState<
    IShallowSolarPlantView[]
  >([]);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedSolarPlant, setSelectedSolarPlant] =
    useState<IShallowSolarPlantView>();
  const [solarBrandList, setSolarBrandList] = useState<Option[]>([
    { value: "", label: "", data: "" },
  ]);
  const [solarPlantStatuses, setSolarPlantStatuses] =
    useState<ViewSolarPlantStatusesResponseDTO>();
  const [sort, setSort] = useState(true);
  const [sortByName, setSortByName] = useState(true);
  const [sortByErrors, setSortByErrors] = useState(false);
  const [selectedPlant, setSelectedPlant] =
    useState<IShallowSolarPlantView | null>(null);
  const [selectedInverterBrand, setSelectedInverterBrand] =
    useState<string>("");

  const [triggerGenerationServiceProviders] =
    useLazyViewGenerationServiceProvidersQuery();
  const [
    triggerSolarPlantStatuses,
    { isFetching: isFetchingSolarPlantStatuses },
  ] = useLazyViewSolarPlantStatusesQuery();

  const filterBadgeValue = [
    filters.generationServiceProviderId,
    filters.solarPlantSize,
    filters.plantStatus,
  ].filter(Boolean).length;

  // The list and map page at different sizes, so switching views starts from the first page, but
  // the filters carry over.
  const handleTabChange = (id: number) => {
    dispatch(setSiteViewTabId(id));
    setFilters({ pageNumber: 0 });
  };

  const selectStatus = (plantStatus?: EPlantStatus) => {
    dispatch(setSelectPlantStatus(plantStatus));
    setFilters({ pageNumber: 0 });
  };

  // Search also runs when the box is cleared; an unchanged term leaves the page where it is, which
  // matters on return from a site.
  const onSearch = () => {
    if (searchText !== sitesListFilters.searchValue) {
      setFilters({ searchValue: searchText, pageNumber: 0 });
    }
  };

  const updateFilters = (
    generationServiceProviderId: string,
    solarPlantSize?: ESolarPlantSize,
    plantStatus?: EPlantStatus,
  ) => {
    setFilters({
      generationServiceProviderId: generationServiceProviderId,
      solarPlantSize: solarPlantSize || undefined,
      pageNumber: 0,
    });
    dispatch(setSelectPlantStatus(plantStatus || undefined));
  };

  const customerSolarPlantListRequestParam = useMemo(() => {
    const filterParams: IListAllSolarPlantsRequestDTO = {
      pageNumber: filters.pageNumber,
      pageSize: selectedTabId === 1 ? 10 : 6,
      searchValue: filters.searchValue,
      sortBy: sortByErrors ? "errors" : "plant_name",
      ascending: sort,
    };

    if (filters.generationServiceProviderId) {
      filterParams.generationServiceProviderId =
        filters.generationServiceProviderId;
    }

    if (filters.solarPlantSize) {
      filterParams.solarPlantSize = filters.solarPlantSize;
    }

    if (filters.plantStatus) {
      filterParams.plantStatus = filters.plantStatus;
    }

    return filterParams;
  }, [filters, selectedTabId, sort, sortByErrors]);

  const {
    data: customerSolarPlantListData,
    isFetching: isFetchingCustomerSolarPlantList,
    refetch,
  } = useGetPaginatedCustomerSolarPlantListAdminQuery(
    customerSolarPlantListRequestParam,
  );

  useEffect(() => {
    if (
      shouldUpdate ||
      filters.generationServiceProviderId ||
      filters.plantStatus ||
      filters.solarPlantSize
    ) {
      refetch();
    }
  }, [
    shouldUpdate,
    refetch,
    location.pathname,
    filters.generationServiceProviderId,
    filters.plantStatus,
    filters.solarPlantSize,
  ]);

  const getServiceProvider = useCallback(
    (id: string): ESolarServiceProvider => {
      const findServiceProvider = solarBrandList.find(
        (item) => item.value === id,
      );

      const serviceProvider =
        findServiceProvider?.data?.toUpperCase() as ESolarServiceProvider;

      return serviceProvider;
    },
    [solarBrandList],
  );

  useEffect(() => {
    if (customerSolarPlantListData) {
      const filteringNullLocations =
        customerSolarPlantListData.genericPage.elements.filter((e) => {
          const lat = e.latitude || e.data?.latitude;
          const lng = e.longitude || e.data?.longitude;
          return (
            lat !== null &&
            lat !== undefined &&
            lng !== null &&
            lng !== undefined
          );
        });

      setSolarPlantList(customerSolarPlantListData.genericPage.elements);
      setTotalElements(customerSolarPlantListData.genericPage.totalElements);
      setLocations(
        filteringNullLocations.map((e) => {
          const statusInfo = getSiteStatusInfo(e);
          const sp = getServiceProvider(e.generationServiceProviderId);
          const lat = e.latitude || e.data?.latitude || 0;
          const lng = e.longitude || e.data?.longitude || 0;
          return {
            lat: Number(lat),
            lng: Number(lng),
            name: e.siteAlias ? e.siteAlias : e.plantName,
            id: e.plantId,
            value: e.capacity,
            address: e.address,
            status: e.status,
            statusLabel: statusInfo.label,
            statusColor: statusInfo.color,
            statusBgColor: statusInfo.bgColor,
            statusIcon: statusInfo.icon,
            serviceProvider: sp,
            solarPlant: e,
          };
        }),
      );
    }
  }, [customerSolarPlantListData, getServiceProvider]);

  // The status counts follow every filter except status itself: they are the status tabs, so
  // filtering them by status would zero out all but the selected one.
  useEffect(() => {
    triggerSolarPlantStatuses({
      extended: false,
      searchValue: filters.searchValue || undefined,
      generationServiceProviderId:
        filters.generationServiceProviderId || undefined,
      solarPlantSize: filters.solarPlantSize,
    })
      .unwrap()
      .then((res: ViewSolarPlantStatusesResponseDTO) => {
        setSolarPlantStatuses(res);
      })
      .catch(() => {
        setSolarPlantStatuses(undefined);
      });
  }, [
    triggerSolarPlantStatuses,
    filters.searchValue,
    filters.generationServiceProviderId,
    filters.solarPlantSize,
  ]);

  useEffect(() => {
    triggerGenerationServiceProviders()
      .unwrap()
      .then((res: IViewAllGenerationServiceProvidersResponseDTO) => {
        const mappingSolarBrandList = res.generationServiceProviders.map(
          (g: IGenerationServiceProviderView) => ({
            value: g.identity,
            label: g.name,
            data: g.shortName,
          }),
        );
        setSolarBrandList(mappingSolarBrandList);
      })
      .catch(() => {
        setSolarBrandList([]);
      });
  }, [triggerGenerationServiceProviders]);

  return (
    <div className="position-relative">
      <Row>
        <Col className="col-12 col-sm">
          <Row className="align-items-center gy-3">
            <Col className="col-12 col-sm-7 col-xl-4 pe-4 pe-sm-2">
              <Search
                onSearch={onSearch}
                handleSearchChange={(searchText) => {
                  setSearchText(searchText);
                }}
                placeholder="Search by name or address"
                value={searchText}
              />
            </Col>
            <Col className="col-4 col-sm-auto">
              <TabsIconButton
                buttons={[
                  { iconName: "format_list_bulleted", id: 1 },
                  { iconName: "distance", id: 2 },
                ]}
                onChange={handleTabChange}
                selectedTabId={selectedTabId}
              />
            </Col>
            {!isGuest && (
              <Col className="col-4 d-block d-sm-none px-0">
                <ButtonWithBadge
                  text="Error Log"
                  icon="emergency_home"
                  iconColor="#69768B"
                  onClick={() => navigate(AppRoute.ADMIN_SITES_ERROR_LOG)}
                />
              </Col>
            )}
            <Col className="col-4 d-block d-sm-none">
              <ButtonWithBadge
                badgeValue={filterBadgeValue}
                text="Filter"
                icon="filter_alt"
                onClick={() => setShowFilteringSidePanel(true)}
              />
            </Col>
          </Row>
        </Col>
        {!isGuest && (
          <Col className="col-auto d-none d-sm-block pe-0">
            <ButtonWithBadge
              text="Error Log"
              icon="emergency_home"
              iconColor="#69768B"
              onClick={() => navigate(AppRoute.ADMIN_SITES_ERROR_LOG)}
            />
          </Col>
        )}
        <Col className="col-auto d-none d-sm-block">
          <ButtonWithBadge
            badgeValue={filterBadgeValue}
            text="Filter"
            icon="filter_alt"
            onClick={() => setShowFilteringSidePanel(true)}
          />
        </Col>
      </Row>
      {selectedTabId === 1 ? (
        <>
          <div className={`${styles.siteStatusCardSContainer} p-2 mt-4`}>
            <Row className="row-cols-1 row-cols-sm-2 row-cols-xl-4 g-2">
              <Col>
                <SiteStatusCount
                  title="All Sites"
                  count={solarPlantStatuses?.numberOfTotalPlants || 0}
                  onClick={() => selectStatus(undefined)}
                  icon="solar_power"
                  iconColor="#469CD9"
                  isLoading={isFetchingSolarPlantStatuses}
                  selected={selectedPlantStatus === undefined}
                />
              </Col>
              <Col>
                <SiteStatusCount
                  title="Active Sites"
                  count={solarPlantStatuses?.numberOfOnlinePlants || 0}
                  onClick={() => selectStatus(EPlantStatus.ACTIVE)}
                  icon="fiber_manual_record"
                  iconColor="#29CC39"
                  isLoading={isFetchingSolarPlantStatuses}
                  selected={selectedPlantStatus === EPlantStatus.ACTIVE}
                />
              </Col>
              <Col>
                <SiteStatusCount
                  title="Offline Sites"
                  count={solarPlantStatuses?.numberOfOfflinePlants || 0}
                  onClick={() => selectStatus(EPlantStatus.OFFLINE)}
                  icon="wifi_off"
                  iconColor="#7D7B86"
                  isLoading={isFetchingSolarPlantStatuses}
                  selected={selectedPlantStatus === EPlantStatus.OFFLINE}
                />
              </Col>
              {/* A monitoring guest is not shown alerts; the backend sends no count. */}
              {!isGuest && (
                <Col>
                  <SiteStatusCount
                    title="Errors"
                    count={solarPlantStatuses?.numberOfAlerts || 0}
                    onClick={() => selectStatus(EPlantStatus.FAULT)}
                    icon="emergency_home"
                    iconColor="#E45E5E"
                    isLoading={isFetchingSolarPlantStatuses}
                    selected={selectedPlantStatus === EPlantStatus.FAULT}
                  />
                </Col>
              )}
            </Row>
          </div>
          {solarPlantList && solarPlantList.length > 0 ? (
            <>
              <Row className="mt-4">
                <Col>
                  <SitesTable
                    headers={[
                      {
                        id: 1,
                        columnName: "Name",
                        isSort: sortByName,
                        sortFunction: () => {
                          setSort((ps) => !ps);
                          setSortByName(true);
                          setSortByErrors(false);
                        },
                        sort: sort,
                      },
                      { id: 2, columnName: "Site Status" },
                      { id: 3, columnName: "Load" },
                      { id: 4, columnName: "Solar" },
                      { id: 5, columnName: "Grid" },
                      { id: 6, columnName: "Battery" },
                      { id: 7, columnName: "Generator" },
                      { id: 8, columnName: "Temperature" },
                      { id: 9, columnName: "Last Update" },
                      {
                        id: 10,
                        columnName: "Errors",
                        isSort: sortByErrors,
                        sortFunction: () => {
                          setSort((ps) => !ps);
                          setSortByErrors(true);
                          setSortByName(false);
                        },
                        sort: sort,
                      },
                    ]}
                    data={solarPlantList.map((solarPlant) => {
                      const serviceProvider = getServiceProvider(
                        solarPlant.generationServiceProviderId,
                      );

                      let generationValue: number | undefined;
                      if (serviceProvider === ESolarServiceProvider.VRM) {
                        generationValue = solarPlant.data.extended?.find(
                          (item) => item.code === "solar_yield",
                        )?.rawValue;
                      } else if (
                        serviceProvider === ESolarServiceProvider.GROWATT
                      ) {
                        generationValue = Number(solarPlant.data.currentPower);
                      } else {
                        generationValue = solarPlant.data.pac;
                      }

                      const statusInfo = getSiteStatusInfo(solarPlant);
                      const sector = sectorLabelOf(
                        siteFacets,
                        siteTagsOf(solarPlant),
                      );

                      return {
                        data: [
                          <Row
                            key="site"
                            className="ps-3 pe-2 align-content-center cursor-pointer h-100"
                            onClick={() =>
                              navigate(
                                AppRoute.ADMIN_SITES + "/" + solarPlant.plantId,
                                {
                                  state: {
                                    id: solarPlant.plantId,
                                    serviceProvider,
                                    name:
                                      solarPlant.siteAlias ||
                                      solarPlant.plantName,
                                  },
                                },
                              )
                            }
                          >
                            <Col>
                              <Row>
                                <Col className="text-dark font-weight-500 font-size-13">
                                  {solarPlant.siteAlias
                                    ? solarPlant.siteAlias
                                    : solarPlant.plantName || "-"}
                                </Col>
                              </Row>
                              <Row className="align-items-center row-cols-auto">
                                <Col className="pe-0">
                                  <img
                                    src={getServiceProviderLogo(
                                      getServiceProvider(
                                        solarPlant.generationServiceProviderId,
                                      ),
                                    )}
                                    alt=""
                                    width={
                                      getServiceProvider(
                                        solarPlant.generationServiceProviderId,
                                      ) === ESolarServiceProvider.GROWATT
                                        ? 15
                                        : 18
                                    }
                                    height={
                                      getServiceProvider(
                                        solarPlant.generationServiceProviderId,
                                      ) === ESolarServiceProvider.GROWATT
                                        ? 15
                                        : 18
                                    }
                                  />
                                </Col>
                                <Col className="ps-2 pe-0 text-light font-weight-400 font-size-12">
                                  {solarPlant.address}
                                </Col>
                                {sector && (
                                  <Col className="ps-2 pe-0 text-light font-weight-400 font-size-12">
                                    {sector}
                                  </Col>
                                )}
                                <Col className="ps-1">
                                  <ActiveInactiveIndicator
                                    isActive={
                                      solarPlant.status ===
                                        EPlantStatus.GENERATING ||
                                      solarPlant.status ===
                                        EPlantStatus.ACTIVE ||
                                      solarPlant.status === EPlantStatus.WAITING
                                    }
                                  />
                                </Col>
                              </Row>
                            </Col>
                          </Row>,
                          <Row
                            key="status"
                            className="align-content-center justify-content-center h-100"
                          >
                            <Col className="col-auto">
                              <div
                                className={styles.siteStatusBadge}
                                style={{
                                  backgroundColor: statusInfo.bgColor,
                                  color: statusInfo.color,
                                }}
                              >
                                <MaterialIcon
                                  icon={statusInfo.icon}
                                  color={statusInfo.color}
                                  size={14}
                                />
                                {statusInfo.label}
                              </div>
                            </Col>
                          </Row>,
                          <Row
                            key="consumption"
                            className="align-content-end h-100"
                          >
                            <Col>
                              <SiteEnergySource
                                data={chartData}
                                value1={
                                  solarPlant.data.extended?.find(
                                    (item) => item.code === "consumption",
                                  )?.rawValue
                                }
                                stopColor="#E4515114"
                                stroke="#D2595959"
                                id="colorUv"
                                isShowTooltip={false}
                              />
                            </Col>
                          </Row>,
                          <Row
                            key="generation"
                            className="align-content-end h-100"
                          >
                            <Col>
                              <SiteEnergySource
                                data={chartData}
                                value1={generationValue}
                                stopColor="#29CC3914"
                                stroke="#59D26B59"
                                id="colorUx"
                                isShowTooltip={false}
                              />
                            </Col>
                          </Row>,
                          <Row key="grid" className="align-content-end h-100">
                            <Col>
                              <SiteEnergySource
                                data={chartData}
                                value1={
                                  solarPlant.data.extended?.find(
                                    (item) => item.code === "from_to_grid",
                                  )?.rawValue
                                }
                                stopColor="#CC882914"
                                stroke="#CC882959"
                                id="colorUz"
                                isShowTooltip={false}
                              />
                            </Col>
                          </Row>,
                          <Row
                            key="battery"
                            className="align-content-end h-100"
                          >
                            <Col>
                              <SiteEnergySource
                                data={chartData}
                                value1={
                                  getServiceProvider(
                                    solarPlant.generationServiceProviderId,
                                  ) === ESolarServiceProvider.VRM
                                    ? solarPlant.data.extended?.find(
                                        (item) => item.code === "bs",
                                      )?.rawValue
                                    : solarPlant.data.battery_soc
                                }
                                value2={
                                  getServiceProvider(
                                    solarPlant.generationServiceProviderId,
                                  ) === ESolarServiceProvider.VRM
                                    ? `${
                                        solarPlant.data.extended?.find(
                                          (item) => item.code === "bv",
                                        )?.rawValue
                                      } V`
                                    : `${solarPlant.data.battery_power} W`
                                }
                                batteryStatus={
                                  solarPlant.data.extended?.find(
                                    (item) => item.code === "bst",
                                  )?.formattedValue
                                }
                                stopColor="#4C29CC14"
                                stroke="#4C29CC59"
                                isBattery
                                id="colorUc"
                                isShowTooltip={false}
                              />
                            </Col>
                          </Row>,
                          <Row
                            key="generator"
                            className="align-content-end h-100"
                          >
                            <Col>
                              <SiteEnergySource
                                data={chartData}
                                value1={
                                  solarPlant.data.extended?.find(
                                    (item) => item.code === "generator",
                                  )?.rawValue
                                }
                                stopColor="#A4579614"
                                stroke="#A4579680"
                                id="colorUq"
                                isShowTooltip={false}
                              />
                            </Col>
                          </Row>,
                          <Row
                            key="temperature"
                            className="row-cols-auto align-items-center justify-content-center h-100"
                          >
                            {Object.entries(
                              solarPlant?.data?.tst?.instances ?? {},
                            ).map(([key, tstInstance], index) => {
                              const tsTInstance = (
                                solarPlant?.data?.tsT?.instances as Record<
                                  string,
                                  IInstance
                                >
                              )?.[key];

                              const length = Object.entries(
                                solarPlant?.data?.tst?.instances ?? {},
                              ).length;

                              return tsTInstance ? (
                                <>
                                  <Col
                                    key={index}
                                    className="text-light font-weight-400 font-size-10"
                                  >
                                    <div>{tstInstance.formattedValue}</div>
                                    <div>
                                      {tsTInstance?.formattedValue ?? "-"}
                                    </div>
                                  </Col>
                                  {index !== length - 1 && (
                                    <Col
                                      className={`${styles.divideLine} pe-0`}
                                    ></Col>
                                  )}
                                </>
                              ) : (
                                <></>
                              );
                            })}
                          </Row>,
                          <Row
                            key="last-updated"
                            className="align-content-center h-100"
                          >
                            <Col className="text-light font-weight-400 font-size-12 text-center">
                              {solarPlant.data.last_timestamp ||
                              solarPlant.data.lastUpdateTime
                                ? getRelativeTimeFromTimestamp(
                                    solarPlant.data.last_timestamp ||
                                      solarPlant.data.lastUpdateTime,
                                  )
                                : `-`}
                            </Col>
                          </Row>,
                          solarPlant.hasErrors ? (
                            <Row
                              key="errors"
                              className="align-content-center justify-content-center h-100 cursor-pointer"
                              onClick={() =>
                                navigate(
                                  AppRoute.ADMIN_SITES +
                                    "/" +
                                    solarPlant.plantId,
                                  {
                                    state: {
                                      id: solarPlant.plantId,
                                      serviceProvider: getServiceProvider(
                                        solarPlant.generationServiceProviderId,
                                      ),
                                      activeTab: 3,
                                      name:
                                        solarPlant.siteAlias ||
                                        solarPlant.plantName,
                                    },
                                  },
                                )
                              }
                            >
                              <Col className="col-auto">
                                <MaterialIcon
                                  icon="emergency_home"
                                  color="#E45E5E"
                                  size={20}
                                  weight={600}
                                  className="user-select-none"
                                />
                              </Col>
                            </Row>
                          ) : (
                            <Fragment key="errors" />
                          ),
                        ],
                        cellWidths: [
                          200, 118, 118, 118, 118, 118, 118, 108, 122, 88,
                        ],
                      };
                    })}
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <Pagination
                    itemsPerPage={10}
                    length={totalElements}
                    currentPage={filters.pageNumber + 1}
                    updateCurrentPage={(pn) => {
                      setFilters({ pageNumber: pn - 1 });
                    }}
                  />
                </Col>
              </Row>
            </>
          ) : (
            <Row className="container-dash mt-4 mx-0">
              <Col className="text-center text-light font-size-12">
                There are no&nbsp;
                {filters.plantStatus === EPlantStatus.DISABLED
                  ? `disabled sites`
                  : `sites`}
              </Col>
            </Row>
          )}
        </>
      ) : (
        <SitesMapView
          solarPlantList={solarPlantList}
          selectedSolarPlant={selectedSolarPlant}
          setSelectedSolarPlant={setSelectedSolarPlant}
          getServiceProvider={getServiceProvider}
          totalElements={totalElements}
          pageNumber={filters.pageNumber}
          onPageChange={(pageNumber) => setFilters({ pageNumber })}
          locations={locations}
        />
      )}
      <SitesFilteringSidePanel
        show={showFilteringSidePanel}
        onClose={() => setShowFilteringSidePanel(false)}
        updateFilters={updateFilters}
        filters={filters}
        solarBrandList={solarBrandList}
      />
      <AddUpdateSiteModal
        show={showAddUpdateSiteModal}
        onClose={() => {
          setShowAddUpdateSiteModal(false);
          setSelectedPlant(null);
        }}
        onCancel={() => {
          setShowAddUpdateSiteModal(false);
          setSelectedPlant(null);
        }}
        solarBrandList={solarBrandList}
        onLinkSolarPlant={(brandId) => {
          setSelectedInverterBrand(brandId || "");
          setShowConnectPlantModal(true);
        }}
        selectedPlant={selectedPlant}
        setSelectedPlant={setSelectedPlant}
      />
      <ConnectPlantModal
        show={showConnectPlantModal}
        solarBrandList={solarBrandList}
        initialInverterBrand={selectedInverterBrand}
        onClose={() => {
          setShowConnectPlantModal(false);
          setShowAddUpdateSiteModal(true);
        }}
        onCancel={() => {
          setShowConnectPlantModal(false);
          setShowAddUpdateSiteModal(true);
        }}
        setSelectedPlant={setSelectedPlant}
      />
      <SpinnerModal show={isFetchingCustomerSolarPlantList} />
    </div>
  );
};

export default Sites;
